import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import { SHARED } from 'app/models/questions/shared';
import { TextInput } from 'app/components/FormFields/TextInput';
import { TextArea } from 'app/components/FormFields/TextArea';
import { Currency } from 'app/components/FormFields/Currency';
import { SwitchToggle } from 'app/components/FormFields/SwitchToggle';
import { Address } from 'app/components/FormFields/Address';
import { State } from 'app/components/FormFields/State';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { Select } from 'app/components/FormFields/Select';
import { SinglePastDate } from 'app/components/FormFields/SinglePastDate';
import { Email } from 'app/components/FormFields/Email';
import { Name } from 'app/components/FormFields/Name';
import { Phone } from 'app/components/FormFields/Phone';
import { RadioField } from 'app/components/FormFields/RadioField';
import { IntegerWithSeparators } from 'app/components/FormFields/IntegerWithSeparators';
import { NAME_SUFFIX_OPTIONS, YES_OR_NO_OPTIONS } from 'app/models/options/options';
import { hasCoBorrower } from 'app/models/fields/conditionals';
import { LegalTitleHoldersComponent } from 'app/components/FormFieldArrays/Dynamic/LegalTitleHolders';
import { LienHoldersComponent } from 'app/components/FormFieldArrays/Dynamic/LienHolders';

export const getCommercialLeaseAgFacilitiesQuestionByName = (name: string): Question => {
  return COMMERCIAL_LEASE_AG_FACILITIES_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

export const getCommercialLeaseAgFacilitiesQuestionsByName = (names: string[] = []): Question[] => {
  return names.map(getCommercialLeaseAgFacilitiesQuestionByName);
};

export const COMMERCIAL_LEASE_AG_FACILITIES_QUESTIONS: Question[] = [
  {
    ...SHARED[FieldNames.firstName],
    id   : 1001,
    label: 'First Name',
    title: 'Tell us about the applicant',
  },
  {
    id        : 1002,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.middleName,
  },
  {
    ...SHARED[FieldNames.lastName],
    id   : 1003,
    label: 'Last Name',
  },
  {
    id        : 1004,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.suffixName,
    options   : NAME_SUFFIX_OPTIONS,
  },
  {
    ...SHARED[FieldNames.email],
    id   : 1005,
    label: 'Email',
  },
  {
    ...SHARED[FieldNames.phone],
    id   : 1006,
    label: 'Phone',
  },
  {
    id    : 1007,
    component: Address,
    label : 'Street Address',
    name  : FieldNames.applicantStreet,
    title : 'Where does the applicant live?',
  },
  {
    id        : 1008,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.applicantStreet2,
  },
  {
    id       : 1009,
    component: Address,
    label    : 'City',
    name     : FieldNames.applicantCity,
  },
  {
    id       : 1010,
    component: State,
    label    : 'State',
    name     : FieldNames.applicantState,
  },
  {
    id       : 1011,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.applicantZip,
  },
  {
    id       : 1012,
    component: TextInput,
    label    : 'County',
    name     : FieldNames.applicantCounty,
  },
  {
    id       : 1013,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.occupancyStartDate,
  },
  {
    id       : 1014,
    component: RadioField,
    name     : FieldNames.coBorrowerYN,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will you have a co-applicant?',
  },
  {
    id       : 1015,
    component: Name,
    label    : 'First Name',
    name     : FieldNames.coBorrowerFirstName,
    showIf   : [hasCoBorrower],
  },
  {
    id        : 1016,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.coBorrowerMiddleName,
    showIf    : [hasCoBorrower],
  },
  {
    id       : 1017,
    component: Name,
    label    : 'Last Name',
    name     : FieldNames.coBorrowerLastName,
    showIf   : [hasCoBorrower],
  },
  {
    id        : 1018,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.coBorrowerSuffixName,
    options   : NAME_SUFFIX_OPTIONS,
    showIf    : [hasCoBorrower],
  },
  {
    id       : 1019,
    component: TextInput,
    label    : 'Major Ag Product',
    name     : FieldNames.majorAgProduct,
    title    : 'Tell us about your farming operation',
  },
  {
    id       : 1020,
    component: Currency,
    label    : 'Gross Farm Income',
    name     : FieldNames.grossFarmIncome,
  },
  {
    id        : 1021,
    component : Currency,
    isOptional: true,
    label     : 'Non-Farm Income',
    name      : FieldNames.nonFarmIncome,
  },
  {
    id       : 1022,
    component: Currency,
    label    : 'Total Assets',
    name     : FieldNames.totalAssets,
  },
  {
    id       : 1023,
    component: Currency,
    label    : 'Total Liabilities',
    name     : FieldNames.totalLiabilities,
  },
  {
    id        : 1024,
    component : TextInput,
    isOptional: true,
    label     : 'County',
    name      : FieldNames.realEstateCounty,
    title     : 'What is the legal description of real estate where the facility will be located?',
  },
  {
    id        : 1025,
    component : TextInput,
    isOptional: true,
    label     : 'Township',
    name      : FieldNames.realEstateTownship,
  },
  {
    id        : 1026,
    component : TextInput,
    isOptional: true,
    label     : 'Section',
    name      : FieldNames.realEstateSection,
  },
  {
    id       : 1027,
    component: LegalTitleHoldersComponent,
    name     : FieldNames.legalTitleHolderList,
    title    : 'Who are the legal title holders to the real estate?',
  },
  {
    id        : 1031,
    component : LienHoldersComponent,
    isOptional: true,
    name      : FieldNames.lienHolderList,
    title     : 'All lien holders on real estate (if applicable)',
  },
  {
    id       : 1035,
    component: TextArea,
    label    : 'Use of Facility and Dimensions',
    name     : FieldNames.facilityUseAndDimensions,
  },
  {
    id       : 1036,
    component: Currency,
    label    : 'Projected Total Cost of Project',
    name     : FieldNames.projectedProjectCost,
  },
  {
    id       : 1037,
    component: TextInput,
    label    : 'Insurance Company Name',
    name     : FieldNames.insuranceCompanyName,
  },
  {
    id       : 1038,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.insuranceCompanyStreet,
    title    : 'Where is the insurance company located?',
  },
  {
    id        : 1039,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.insuranceCompanyStreet2,
  },
  {
    id       : 1040,
    component: Address,
    label    : 'City',
    name     : FieldNames.insuranceCompanyCity,
  },
  {
    id       : 1041,
    component: State,
    label    : 'State',
    name     : FieldNames.insuranceCompanyState,
  },
  {
    id       : 1042,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.insuranceCompanyZip,
  },
  {
    id       : 1043,
    component: Name,
    label    : 'First Name',
    name     : FieldNames.insuranceAgentFirstName,
    title    : 'Tell us about the insurance agent',
  },
  {
    id        : 1044,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.insuranceAgentMiddleName,
  },
  {
    id       : 1045,
    component: Name,
    label    : 'Last Name',
    name     : FieldNames.insuranceAgentLastName,
  },
  {
    id        : 1046,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.insuranceAgentSuffixName,
    options   : NAME_SUFFIX_OPTIONS,
  },
  {
    id       : 1047,
    component: Email,
    label    : 'Email',
    name     : FieldNames.insuranceAgentEmail,
  },
  {
    id       : 1048,
    component: Phone,
    label    : 'Phone',
    name     : FieldNames.insuranceAgentPhone,
  },
  {
    id       : 1049,
    component: SwitchToggle,
    label    : 'Is the facility site along a public road?',
    name     : FieldNames.facilityAlongPublicRoad,
  },
  {
    id       : 1050,
    component: SwitchToggle,
    label    : 'Will the facility site have its own well and electrical hookup?',
    name     : FieldNames.facilityHasWellAndElectric,
  },
  {
    id       : 1051,
    component: SwitchToggle,
    label    : 'Is the facility site currently free of any existing buildings or other fixtures?',
    name     : FieldNames.facilityFreeOfExistingBuildings,
  },
  {
    id       : 1052,
    component: SwitchToggle,
    label    : 'Is the facility site free of any mortgages or other liens against the property?',
    name     : FieldNames.facilityFreeOfMortgages,
  },
  {
    id       : 1053,
    component: SwitchToggle,
    label    : 'Have all appropriate pollution control permits been obtained?',
    name     : FieldNames.pollutionControlPermitsObtained,
  },
  {
    id       : 1054,
    component: SwitchToggle,
    label    : 'Have all appropriate zoning permits (including construction permits) been obtained?',
    name     : FieldNames.zoningPermitsObtained,
  },
  {
    id       : 1055,
    component: SwitchToggle,
    label    : 'Has a survey been completed on the facility site?',
    name     : FieldNames.surveyCompleted,
  },
  {
    id       : 1056,
    component: IntegerWithSeparators,
    title    : 'Approximately how many acres will the facility site include?',
    label    : 'Acres',
    name     : FieldNames.facilityAcreage,
  },
  {
    id       : 1057,
    component: Name,
    label    : 'First Name',
    name     : FieldNames.recordOwnerFirstName,
    title    : 'Who is the record owner of the facility site?',
  },
  {
    id        : 1058,
    component : Name,
    isOptional: true,
    label     : 'Middle Name',
    name      : FieldNames.recordOwnerMiddleName,
  },
  {
    id       : 1059,
    component: Name,
    label    : 'Last Name',
    name     : FieldNames.recordOwnerLastName,
  },
  {
    id        : 1060,
    component : Select,
    isOptional: true,
    label     : 'Suffix',
    name      : FieldNames.recordOwnerSuffixName,
    options   : NAME_SUFFIX_OPTIONS,
  },
  {
    id       : 1061,
    component: SwitchToggle,
    label    : 'Are there any judgements or state or federal tax liens of record against you?',
    name     : FieldNames.hasTaxLiens,
  },
  {
    id       : 1062,
    component: SwitchToggle,
    label    : 'Have you been a debtor in bankruptcy in the last 10 years?',
    name     : FieldNames.hasBankruptcy,
  },
  {
    id       : 1063,
    component: SwitchToggle,
    label    : 'Are you a party to a lawsuit?',
    name     : FieldNames.isPartyToLawsuit,
  },
  {
    id       : 1064,
    component: SwitchToggle,
    label    : 'Are any of your taxes delinquent or under dispute?',
    name     : FieldNames.hasTaxDelinquency,
  },
  {
    id       : 1065,
    component: SwitchToggle,
    label    : 'Are you obligated to pay as a cosigner or guarantor on any other obligations?',
    name     : FieldNames.isCosignerOrGuarantor,
  },
  {
    id       : 1066,
    component: SwitchToggle,
    label    : 'Are you obligated to pay alimony, child support or separate maintenance?',
    name     : FieldNames.paysAlimony,
  },
  {
    id       : 1067,
    component: SwitchToggle,
    label    : 'Does anyone else own an interest in the property on the balance sheet?',
    name     : FieldNames.othersOwnBalanceSheetInterest,
  },
  {
    id       : 1068,
    component: SwitchToggle,
    label    : 'Are there underground storage tanks (if yes, describe size, contents, age), abandoned wells or hazardous waste sites?',
    name     : FieldNames.hasUndergroundStorageTanks,
  },
  {
    id       : 1069,
    component: SwitchToggle,
    label    : 'Has there been any significant construction on the facility site in the last six months?',
    name     : FieldNames.hasRecentSignificantConstruction,
  },
  {
    id       : 1070,
    component: SwitchToggle,
    label    : 'Is any of your property on any government hazardous waste list?',
    name     : FieldNames.isOnGovtHazardWasteList,
  },
  {
    id       : 1071,
    component: SwitchToggle,
    label    : 'Have you improperly disposed of any hazardous substances on your property or elsewhere?',
    name     : FieldNames.hasImproperlyDisposedHazardous,
  },
  {
    id       : 1072,
    component: SwitchToggle,
    label    : 'Are you aware of any potential hazardous waste located on your farm?',
    name     : FieldNames.awareOfFarmHazardousWaste,
  },
];
